import { findProductById } from '../config/products.js'
import { findProductFacts } from '../config/productFacts.js'

/**
 * @typedef {import('../config/products.js').ProductCopy} ProductCopy
 * @typedef {'home'|'about'|'products'|'product'|'quality'|'faq'|'contact'|'add-friend'} PageKind
 * @typedef {{ label: string, path: string }} ContentLink
 * @typedef {Object} PageDefinition
 * @property {PageKind} kind
 * @property {string} path 静态托管路径，例如 /products.html
 * @property {string} title
 * @property {string} description
 * @property {string} heading
 * @property {string} [intro]
 * @property {string} lang
 * @property {string} canonical
 * @typedef {import('../config/productFacts.js').ProductFacts} ProductFacts
 * @property {ProductCopy} [product]
 * @property {ProductFacts} [facts]
 */

export const BRAND_NAME = '滋仙草'
export const SITE_ORIGIN = 'https://www.zixiancao.com'

/** 品牌 logo 与公司介绍视频沿用原站素材，构建期只引用，不下载。 */
const MEDIA_BASE = 'https://zixiancao-1306805299.cos.ap-guangzhou.myqcloud.com'
export const BRAND_LOGO_URL = `${MEDIA_BASE}/images/logo.png`
export const COMPANY_VIDEO_URL = `${MEDIA_BASE}/videos/company-intro.mp4`

// 旧的“全站首访自动跳转”标记键。新的自动跳转只发生在专用获客页且使用新键，
// 旧标记既不消耗新键，也不会被新页面写入。
export const RETIRED_AUTO_NAV_STORAGE_KEY = 'face.contact.auto-nav-attempted.v1'

export const CONTACT_PAGE_PATH = '/add-friend.html'
export const CONTACT_PAGE_ROUTE = '/add-friend'

/** 获客页只是投放落地页，不进 sitemap、不抢规范网址，但要有自己的 title/description。 */
export const CONTACT_PAGE = {
  kind: 'add-friend',
  path: CONTACT_PAGE_PATH,
  title: '添加企业顾问 | 滋仙草官网',
  description: '添加滋仙草企业顾问：微信扫码或点击按钮，添加后可以咨询石斛等滋补产品的规格、选购与购买事宜。',
  heading: '添加滋仙草企业顾问'
}

/** @type {string[]} */
export const PUBLIC_PAGE_PATHS = [
  '/',
  '/about.html',
  '/products.html',
  '/products/shihu1.html',
  '/products/shihu2.html',
  '/products/shihu3.html',
  '/products/shihu4.html',
  '/products/tianma1.html',
  '/products/tianma2.html',
  '/quality.html',
  '/faq.html',
  '/contact.html'
]

/** @type {ContentLink[]} */
export const NAV_LINKS = [
  { label: '首页', path: '/' },
  { label: '品牌故事', path: '/about.html' },
  { label: '匠心产品', path: '/products.html' },
  { label: '品质信息', path: '/quality.html' },
  { label: '常见问题', path: '/faq.html' },
  { label: '联系我们', path: '/contact.html' }
]

/**
 * 旧 hash 链接到新的真实 .html 对象：唯一映射表，路由与导航都从这里取。
 * @type {Record<string, string>}
 */
export const LEGACY_HASH_MAP = {
  '/': '/',
  '/brand-story': '/about.html',
  '/products': '/products.html',
  '/quality': '/quality.html',
  '/faq': '/faq.html',
  '/contact': '/contact.html',
  '/add-friend': '/add-friend.html'
}

/** 首页锚点仍指向首页自身的区块 id。 */
export const HOME_ANCHORS = ['brand-story', 'products', 'quality', 'contact']

/**
 * @param {string} hashPath 形如 /brand-story 或空
 * @returns {string | null}
 */
export function legacyTargetFor(hashPath) {
  if (typeof hashPath !== 'string') return null
  return LEGACY_HASH_MAP[hashPath] || null
}

/**
 * 旧链接改写：`#/brand-story` 一类的 hash 路由改写到真实页面，普通首页锚点
 * （`#contact` 等）连同 hash 原样保留，否则会丢掉锚点并停在第 0 屏。
 * @param {string} to
 * @returns {string}
 */
export function resolveLegacyHash(to) {
  const hash = to.startsWith('/#') ? to.slice(2) : null
  if (hash === null) return to
  if (hash === '') return '/'
  const [hashPath, ...rest] = hash.split('#')
  if (hashPath === '' && rest.length > 0) {
    const anchor = rest.join('#')
    return HOME_ANCHORS.includes(anchor) ? `/#${anchor}` : to
  }
  const target = legacyTargetFor(hashPath)
  if (!target) return to
  return rest.length > 0 ? `${target}#${rest.join('#')}` : target
}

const PATH_PATTERN = /^\/(?:[a-z0-9][a-z0-9-]*\.html|[a-z0-9][a-z0-9-]*\/[a-z0-9][a-z0-9-]*\.html)$/

/**
 * 静态托管路径 -> 站内路由（去掉 .html）；非法或上级路径返回 null。
 * @param {string} path
 * @returns {string | null}
 */
export function publicRouteForPath(path) {
  if (path === '/') return '/'
  if (typeof path !== 'string' || !PATH_PATTERN.test(path)) return null
  return path.slice(0, -'.html'.length)
}

/**
 * 站内路由 -> 自指 canonical；非法路由返回 null。
 * @param {string} route
 * @returns {string | null}
 */
export function buildCanonicalUrl(route) {
  if (typeof route !== 'string' || /[\s<>"'?#]/.test(route)) return null
  if (route === '/') return `${SITE_ORIGIN}/`
  if (publicRouteForPath(`${route}.html`) === null) return null
  return `${SITE_ORIGIN}${route}.html`
}

/**
 * 站内路由 -> 需要落盘的 HTML 文件名（相对 dist）。
 * @param {string} path
 * @returns {string | null}
 */
export function pageFileForPath(path) {
  const route = publicRouteForPath(path)
  if (route === null) return null
  return route === '/' ? 'index.html' : `${route.slice(1)}.html`
}

/**
 * 页面 SEO 元数据注册表：title/description/canonical 与结构化数据都从这里取。
 * 客户可见的正文写在对应 Vue 组件里，不在这里复写第二套 DOM。
 * @returns {PageDefinition[]}
 */
function pageDefinitions() {
  return [
    {
      kind: 'home',
      path: '/',
      title: '滋仙草官网 | 云南石斛与中式滋补品牌',
      description:
        '滋仙草官网：云南石斛与天麻原浆品牌，六款规格的净含量、袋数与配料可直接对照，欢迎联系企业顾问了解详情。',
      heading: '滋仙草 · 云南石斛中式滋补品牌'
    },
    {
      kind: 'about',
      path: '/about.html',
      title: '品牌故事 | 滋仙草官网',
      description: '滋仙草品牌故事：扎根云南的石斛滋补品牌，从龙陵的种植基地出发，逐步形成石斛、三七、灵芝与精油四大产品系列。',
      heading: '品牌故事'
    },
    {
      kind: 'products',
      path: '/products.html',
      title: '匠心产品 | 石斛原浆与天麻原浆系列参数对比 | 滋仙草官网',
      description:
        '滋仙草产品系列：紫皮石斛原浆、铁皮石斛原浆与天麻原浆各规格的净含量、配料、保质期、贮存与食用方法对照表，六款可按包装标示值直接比较。',
      heading: '匠心产品'
    },
    {
      kind: 'product',
      path: '/products/shihu1.html',
      title: '紫皮石斛原浆礼盒装 210mL（30mL×7袋）| 配料与食用方法 | 滋仙草官网',
      description:
        '紫皮石斛原浆饮礼盒装：净含量 210mL（30mL×7 袋），配料紫皮石斛浆与木糖醇，保质期 18 个月，开袋即饮。参数照录包装标签，附图片与警示。',
      heading: '紫皮石斛原浆礼盒装'
    },
    {
      kind: 'product',
      path: '/products/shihu2.html',
      title: '紫皮石斛原浆量贩装 900mL（30mL×30袋）| 配料与食用方法 | 滋仙草官网',
      description:
        '紫皮石斛原浆饮量贩装：净含量 900mL（30mL×30 袋），配料紫皮石斛浆与木糖醇，保质期 18 个月，开袋即饮。参数照录包装标签，附包装正面图片。',
      heading: '紫皮石斛原浆量贩装'
    },
    {
      kind: 'product',
      path: '/products/shihu3.html',
      title: '铁皮石斛原浆礼盒装 210mL（30mL×7袋）| 配料与食用方法 | 滋仙草官网',
      description:
        '铁皮石斛原浆饮盒装：净含量 210mL（30mL×7 袋），配料铁皮石斛浆与木糖醇，保质期 18 个月，开袋即饮。与紫皮石斛原浆按同一张表逐项比较。',
      heading: '铁皮石斛原浆礼盒装'
    },
    {
      kind: 'product',
      path: '/products/shihu4.html',
      title: '铁皮石斛原浆量贩装 900mL（30mL×30袋）| 配料与食用方法 | 滋仙草官网',
      description:
        '铁皮石斛原浆饮量贩装：净含量 900mL（30mL×30 袋），配料铁皮石斛浆与木糖醇，保质期 18 个月，开袋即饮。参数照录包装标签正面与信息面。',
      heading: '铁皮石斛原浆量贩装'
    },
    {
      kind: 'product',
      path: '/products/tianma1.html',
      title: '有机天麻原浆礼盒装 210mL（30mL×7袋）| 配料与食用方法 | 滋仙草官网',
      description:
        '天麻原浆饮盒装：净含量 210mL（30mL×7 袋），配料乌天麻浆与低聚果糖，保质期 18 个月，开袋即饮。参数照录包装正面与信息面，附图片与警示。',
      heading: '有机天麻原浆礼盒装'
    },
    {
      kind: 'product',
      path: '/products/tianma2.html',
      title: '有机天麻原浆量贩装 900mL（30mL×30袋）| 配料与食用方法 | 滋仙草官网',
      description:
        '有机天麻原浆饮量贩装：净含量 900mL（30mL×30 袋），配料乌天麻浆与低聚果糖，保质期 18 个月，直接饮用。附包装正面与信息面图片及警示。',
      heading: '有机天麻原浆量贩装'
    },
    {
      kind: 'quality',
      path: '/quality.html',
      title: '品质信息 | 滋仙草官网',
      description: '滋仙草品质信息：从云南产区的原料种植，到工厂加工与产地溯源直播，了解我们在原料与工艺上的做法。',
      heading: '品质信息'
    },
    {
      kind: 'faq',
      path: '/faq.html',
      title: '常见问题 | 产品信息、购买渠道与联系顾问 | 滋仙草官网',
      description: '滋仙草常见问题：产品规格与配料在哪里查看、如何选购石斛产品、怎样联系企业顾问，以及官网域名与联系方式。',
      heading: '常见问题'
    },
    {
      kind: 'contact',
      path: '/contact.html',
      title: '联系我们 | 服务热线与企业微信 | 滋仙草官网',
      description: '滋仙草联系方式：服务热线 400-831-7518、商务合作邮箱、公司地址与微信公众号，也可以直接添加企业顾问。',
      heading: '联系我们'
    }
  ]
}

export const PAGES = pageDefinitions().map((page) => ({
  ...page,
  lang: 'zh-CN',
  canonical: buildCanonicalUrl(publicRouteForPath(page.path))
}))

/**
 * @param {string} path
 * @returns {PageDefinition | null}
 */
export function resolvePage(path) {
  if (path === CONTACT_PAGE_PATH) return { ...CONTACT_PAGE, lang: 'zh-CN' }
  const definition = PAGES.find((page) => page.path === path)
  if (!definition) return null
  if (definition.kind !== 'product') return definition
  const id = path.replace(/^\/products\//, '').replace(/\.html$/, '')
  const product = findProductById(id)
  const facts = findProductFacts(id)
  return product && facts ? { ...definition, product, facts } : null
}
